import { Link } from "react-router-dom";
import { Phone, Mail } from "lucide-react";
import { services } from "@/lib/data/services";
import { cities } from "@/lib/data/cities";
import { sectors } from "@/lib/data/sectors";
import {
  CONTACT_PHONE,
  CONTACT_PHONE_HREF,
  CONTACT_EMAIL,
  CONTACT_EMAIL_HREF,
} from "@/components/Nav";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#050505] border-t border-[#1e1e1e]"
    >
      {/* Sitemap grid — dense, architectural, SEO-critical */}
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-24 grid grid-cols-12 gap-10">
        <div className="col-span-12 md:col-span-3">
          <p className="font-editorial italic text-[#FF3B30] text-sm mb-4">
            00 — Directory
          </p>
          <h3 className="font-display text-3xl uppercase tracking-tight mb-6">
            Every service.
            <br />
            Every community.
          </h3>
          <p className="font-body text-sm text-[#a0a0a0] max-w-sm leading-relaxed">
            Manitoba Security Services covers the entire province — from
            Winnipeg&apos;s downtown financial core to the fly-in mining camps of
            the Thompson nickel belt.
          </p>
          <ul className="mt-6 space-y-2">
            <li>
              <Link
                to="/contact"
                data-testid="footer-nav-contact"
                className="link-underline font-body text-[13px] uppercase tracking-[0.14em] text-white/80 hover:text-white"
              >
                Contact
              </Link>
            </li>
            <li>
              <Link
                to="/sectors"
                data-testid="footer-nav-sectors"
                className="link-underline font-body text-[13px] uppercase tracking-[0.14em] text-white/80 hover:text-white"
              >
                Sector programs
              </Link>
            </li>
            <li>
              <Link
                to="/playbook"
                data-testid="footer-nav-playbook"
                className="link-underline font-body text-[13px] uppercase tracking-[0.14em] text-white/80 hover:text-white"
              >
                Backlink playbook
              </Link>
            </li>
            <li>
              <Link
                to="/about"
                data-testid="footer-nav-about"
                className="link-underline font-body text-[13px] uppercase tracking-[0.14em] text-white/80 hover:text-white"
              >
                The Standard
              </Link>
            </li>
          </ul>

          <div className="mt-8 pt-6 border-t border-[#1e1e1e] space-y-3">
            <a
              href={CONTACT_PHONE_HREF}
              data-testid="footer-phone"
              className="inline-flex items-center gap-2 font-body text-[13px] uppercase tracking-[0.14em] text-white hover:text-[#FF3B30] transition-colors"
            >
              <Phone size={14} className="text-[#FF3B30]" strokeWidth={2.2} />
              {CONTACT_PHONE}
            </a>
            <br />
            <a
              href={CONTACT_EMAIL_HREF}
              data-testid="footer-email"
              className="inline-flex items-center gap-2 font-body text-[13px] uppercase tracking-[0.14em] text-white hover:text-[#FF3B30] transition-colors break-all"
            >
              <Mail size={14} className="text-[#FF3B30]" strokeWidth={2.2} />
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>

        <div className="col-span-6 md:col-span-3">
          <p className="font-editorial italic text-[#a0a0a0] text-xs mb-4">
            Services
          </p>
          <ul className="space-y-2">
            {services.map((s) => (
              <li key={s.slug}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`footer-service-${s.slug}`}
                  className="link-underline font-body text-[13px] uppercase tracking-[0.14em] text-white/80 hover:text-white"
                >
                  {s.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="col-span-6 md:col-span-3">
          <p className="font-editorial italic text-[#a0a0a0] text-xs mb-4">
            Sector programs
          </p>
          <ul className="space-y-2">
            {sectors.map((s) => (
              <li key={s.slug}>
                <Link
                  to={`/sectors/${s.slug}`}
                  data-testid={`footer-sector-${s.slug}`}
                  className="link-underline font-body text-[12px] uppercase tracking-[0.12em] text-white/70 hover:text-white"
                >
                  {s.heading.replace(". ", " · ").replace(".", "")}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="col-span-12 md:col-span-3">
          <p className="font-editorial italic text-[#a0a0a0] text-xs mb-4">
            Coverage — Manitoba
          </p>
          <ul className="grid grid-cols-2 gap-x-4 gap-y-2">
            {cities.map((c) => (
              <li key={c.slug}>
                <Link
                  to={`/cities/${c.slug}`}
                  data-testid={`footer-city-${c.slug}`}
                  className="link-underline font-body text-[12px] uppercase tracking-[0.12em] text-white/70 hover:text-white"
                >
                  {c.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Massive typographic sign-off */}
      <div className="border-t border-[#1e1e1e]">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-16">
          <h2
            aria-hidden
            className="font-display font-black tracking-tightest leading-[0.82] text-white select-none"
            style={{ fontSize: "clamp(3rem, 12vw, 12rem)" }}
          >
            MAZE
            <br />
            SECURITY
          </h2>
          <p className="mt-4 font-editorial italic text-[#a0a0a0] text-lg md:text-2xl">
            Manitoba Security Services — one operating standard, executed province-wide.
          </p>
        </div>
      </div>

      <div className="border-t border-[#1e1e1e]">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-6 pb-24 md:pb-28 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="font-body text-xs uppercase tracking-[0.16em] text-[#6a6a6a]">
            © {new Date().getFullYear()} Maze Security Solutions / Manitoba
            Security Services — Winnipeg, MB
          </p>
          <p className="font-editorial italic text-sm text-[#6a6a6a]">
            Vigilance. Authority. Discipline.
          </p>
        </div>
      </div>
    </footer>
  );
}
